import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import type { BgMasterListItem } from '@ebalance/proto/bffuser/v1/occto_master_model_pb'
import type { WorkflowGenerationTarget } from '@ebalance/proto/bffuser/v1/workflow_case_service_pb'

import {
  type CivilDate,
  dateToCivilDate,
  protoDateToCivilDate,
  todayCivilDate,
} from '~/models/civilDate'
import { isActiveOn } from '~/models/validPeriod'
import {
  WORKFLOW_TARGET_CODE_KIND_RANK,
  type WorkflowTargetCodeKind,
  workflowTargetCodeKindOfBgLicense,
} from '~/pages/WorkflowCases/models/workflowTargetCodeKind'

/** 候補が有効な日の区間 1 つと、その区間での表示名。 */
export type CandidatePeriod = {
  /** 欠落は暦日不正として、どの日にも有効でない扱いにする (fail-closed)。 */
  validFrom: CivilDate | undefined
  validTo: CivilDate | undefined
  name: string
}

/**
 * 作成ダイアログの BG コード欄の候補 1 つ。BG マスタの行 (period 1 つ) と発電の
 * 計画提出者コード (`ListWorkflowGenerationTargetCandidates` の `targets`。導ける
 * 受渡日の区間列) を同じ形に揃え、エリア・対象日の絞り込みと失効警告を共通にする。
 */
export type WorkflowTargetCandidate = {
  code: string
  areaCode: AreaCode
  /** 昇順・互いに素。いずれかの period で有効な日に候補が有効。 */
  periods: readonly CandidatePeriod[]
  kind: WorkflowTargetCodeKind
}

/**
 * BG コード欄の候補の母集合を作る (spec AC-WFW-003j)。発電の候補を含めるかは
 * 雛形の `allowsGenerationTargetCode` (backend の導出値) で caller が決め、
 * 含めない時は空配列を渡す。並びは 種別 (需要BG → 自己託送BG → 発電) → コード
 * (design `demand/cases.html` `_bgsForArea`)。
 */
export function buildWorkflowTargetCandidates(
  bgMasterItems: ReadonlyArray<
    Pick<
      BgMasterListItem,
      'bgCode' | 'bgName' | 'areaCode' | 'licenseType' | 'validFrom' | 'validTo'
    >
  >,
  generationTargets: ReadonlyArray<
    Pick<WorkflowGenerationTarget, 'submitterCode' | 'areaCode' | 'periods'>
  >,
): WorkflowTargetCandidate[] {
  const bgs = bgMasterItems.flatMap((bg): WorkflowTargetCandidate[] => {
    const kind = workflowTargetCodeKindOfBgLicense(bg.licenseType)
    if (kind === undefined) return []
    return [
      {
        code: bg.bgCode,
        areaCode: bg.areaCode,
        periods: [
          {
            validFrom: protoDateToCivilDate(bg.validFrom),
            validTo: protoDateToCivilDate(bg.validTo),
            name: bg.bgName,
          },
        ],
        kind,
      },
    ]
  })
  const generations = generationTargets.map(
    (g): WorkflowTargetCandidate => ({
      code: g.submitterCode,
      areaCode: g.areaCode,
      periods: g.periods.map((p) => ({
        validFrom: protoDateToCivilDate(p.validFrom),
        validTo: protoDateToCivilDate(p.validTo),
        name: p.submitterName,
      })),
      kind: 'generation',
    }),
  )
  return [...bgs, ...generations].sort(
    (a, b) =>
      WORKFLOW_TARGET_CODE_KIND_RANK[a.kind] -
        WORKFLOW_TARGET_CODE_KIND_RANK[b.kind] ||
      (a.code < b.code ? -1 : a.code > b.code ? 1 : 0),
  )
}

/**
 * 対象日 (未選択時は today 1 件) を `CivilDate[]` へ正規化する。
 * 候補の有効性判定・失効警告の両方が同じ基準日集合を使う (issue #5219 Decisions #8)。
 */
function candidateDates(
  targetDates: readonly Date[],
  today: CivilDate,
): CivilDate[] {
  return targetDates.length > 0 ? targetDates.map(dateToCivilDate) : [today]
}

function isPeriodActiveOn(period: CandidatePeriod, date: CivilDate): boolean {
  const { validFrom, validTo } = period
  if (validFrom === undefined) return false
  return isActiveOn(validFrom, validTo, date)
}

function isActiveOnAnyDate(
  candidate: Pick<WorkflowTargetCandidate, 'periods'>,
  dates: readonly CivilDate[],
): boolean {
  return dates.some((d) =>
    candidate.periods.some((p) => isPeriodActiveOn(p, d)),
  )
}

function compareCivilDate(a: CivilDate, b: CivilDate): number {
  return a.year - b.year || a.month - b.month || a.day - b.day
}

/**
 * 候補の表示名。候補日 (選択済み対象日、無ければ today) を昇順に見て最初に有効な
 * period の名前、どの候補日にも有効でなければ最後の period の名前。
 */
export function candidateNameOn(
  candidate: Pick<WorkflowTargetCandidate, 'periods'>,
  targetDates: readonly Date[],
  today: CivilDate = todayCivilDate(),
): string {
  const dates = candidateDates(targetDates, today).sort(compareCivilDate)
  for (const d of dates) {
    const period = candidate.periods.find((p) => isPeriodActiveOn(p, d))
    if (period !== undefined) return period.name
  }
  return candidate.periods.at(-1)?.name ?? ''
}

/**
 * BG 必須モードの候補を絞り込む (issue #5219 Decisions #8, spec AC-WFW-003):
 * - エリア未選択時は絞り込まない。エリア選択時はそのエリアの候補のみに絞る
 * - いずれの場合も「選択済み対象日のいずれか 1 日がいずれかの period で有効」
 *   (`isActiveOn`) を満たす候補のみにする (対象日未選択時は today 基準)
 */
export function filterBgCandidates<
  T extends Pick<WorkflowTargetCandidate, 'areaCode' | 'periods'>,
>(
  candidates: readonly T[],
  selectedAreaCodes: readonly AreaCode[],
  targetDates: readonly Date[],
  today: CivilDate = todayCivilDate(),
): T[] {
  const dates = candidateDates(targetDates, today)
  return candidates.filter((c) => {
    if (
      selectedAreaCodes.length > 0 &&
      !selectedAreaCodes.includes(c.areaCode)
    ) {
      return false
    }
    return isActiveOnAnyDate(c, dates)
  })
}

/**
 * 選択済みコードのうち、候補日 (選択済み対象日のいずれも無ければ today) の
 * **全て**で失効しているものが 1 つでもあるか判定する (issue #5219
 * Decisions #8: 対象日変更で自動除外はせず、warning のみ出す)。
 */
export function hasExpiredSelectedBg(
  selectedCodes: readonly string[],
  candidates: ReadonlyArray<Pick<WorkflowTargetCandidate, 'code' | 'periods'>>,
  targetDates: readonly Date[],
  today: CivilDate = todayCivilDate(),
): boolean {
  const dates = candidateDates(targetDates, today)
  return selectedCodes.some((code) => {
    const candidate = candidates.find((item) => item.code === code)
    if (candidate === undefined) return false
    return !isActiveOnAnyDate(candidate, dates)
  })
}
